/**
 * Utility functions for API calls and caching
 */
import axios from 'axios';

// Create a configured axios instance with defaults
export const api = axios.create({
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging and timing
api.interceptors.request.use(
  (config) => {
    // Add timestamp to track request duration
    config.metadata = { startTime: new Date() };
    console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for logging and timing
api.interceptors.response.use(
  (response) => {
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`API Response: ${response.config.method.toUpperCase()} ${response.config.url} (${duration}ms)`);
    return response;
  },
  (error) => {
    if (error.response) {
      const duration = new Date() - error.config.metadata.startTime;
      console.error(`API Error: ${error.config.method.toUpperCase()} ${error.config.url} (${duration}ms) - ${error.response.status}`);
    }
    return Promise.reject(error);
  }
);

/**
 * Simple in-memory cache for API responses
 */
const apiCache = {
  cache: {},
  
  /**
   * Get cached response for a key
   * @param {string} key - Cache key
   * @returns {any|null} - Cached value or null
   */
  get(key) {
    const item = this.cache[key];
    if (!item) return null;
    
    // Check if cache has expired
    if (item.expiry && item.expiry < Date.now()) {
      delete this.cache[key];
      return null;
    }
    
    return item.value;
  },
  
  /**
   * Set cache value with optional expiry
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttlSeconds - Time to live in seconds
   */
  set(key, value, ttlSeconds = 300) { // Default 5 minutes
    this.cache[key] = {
      value,
      expiry: ttlSeconds > 0 ? Date.now() + (ttlSeconds * 1000) : null
    };
  },
  
  /**
   * Clear specific cache entry
   * @param {string} key - Cache key to clear
   */
  clear(key) {
    delete this.cache[key];
  },
  
  /**
   * Clear all cache entries
   */
  clearAll() {
    this.cache = {};
  }
};

/**
 * Fetch data with caching
 * @param {string} url - URL to fetch
 * @param {Object} options - Axios options
 * @param {number} cacheTtl - Cache TTL in seconds (0 to disable)
 * @returns {Promise<any>} - Response data
 */
export const fetchWithCache = async (url, options = {}, cacheTtl = 300) => {
  const cacheKey = `${url}-${JSON.stringify(options)}`;
  
  // Check cache first if caching is enabled
  if (cacheTtl > 0) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData) {
      console.log(`Cache hit for ${url}`);
      return cachedData;
    }
  }
  
  try {
    const response = await api(url, options);
    
    // Cache the response if caching is enabled
    if (cacheTtl > 0) {
      apiCache.set(cacheKey, response.data, cacheTtl);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
};

/**
 * Clear API cache for specific URL pattern
 * @param {string|RegExp} urlPattern - URL pattern to match
 */
export const clearApiCache = (urlPattern) => {
  if (!urlPattern) {
    apiCache.clearAll();
    return;
  }
  
  const pattern = urlPattern instanceof RegExp ? urlPattern : new RegExp(urlPattern);
  
  Object.keys(apiCache.cache).forEach(key => {
    if (pattern.test(key)) {
      apiCache.clear(key);
    }
  });
};

export default {
  api,
  fetchWithCache,
  clearApiCache
};
